import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type ChecklistItemProps = {
  label: ReactNode;
  meta?: ReactNode;
  icon?: IconName | string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  style?: CSSProperties;
};

/**
 * ChecklistItem — a tappable row for the kindergarten packing checklist.
 * Shows an icon, label, optional meta ("who packed it"), and a check state.
 */
export function ChecklistItem({
  label,
  meta,
  icon,
  checked,
  defaultChecked,
  onChange,
  disabled = false,
  style,
}: ChecklistItemProps) {
  const isControlled = checked !== undefined;
  const [inner, setInner] = useState(!!defaultChecked);
  const on = isControlled ? checked : inner;
  const [hover, setHover] = useState(false);

  const toggle = () => {
    if (disabled) return;
    if (!isControlled) setInner((v) => !v);
    onChange?.(!on);
  };

  return (
    <div
      role="checkbox"
      aria-checked={on}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        background: on ? 'var(--brand-subtle)' : hover ? 'var(--surface-sunken)' : 'var(--surface)',
        border: `1px solid ${on ? 'transparent' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition:
          'background var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard)',
        ...style,
      }}
    >
      {icon ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-sm)',
            background: on ? 'var(--surface)' : 'var(--surface-sunken)',
            color: on ? 'var(--brand)' : 'var(--text-muted)',
            flexShrink: 0,
          }}
        >
          <Icon name={icon} size={19} />
        </span>
      ) : null}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 'var(--fw-semibold)' as unknown as number,
            fontSize: 'var(--text-md)',
            color: 'var(--text)',
            textDecoration: on ? 'line-through' : 'none',
            textDecorationColor: 'var(--text-subtle)',
          }}
        >
          {label}
        </div>
        {meta ? <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-subtle)' }}>{meta}</div> : null}
      </div>

      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          flexShrink: 0,
          borderRadius: '50%',
          border: on ? '1px solid var(--brand)' : '1px solid var(--border-strong)',
          background: on ? 'var(--brand)' : 'transparent',
          color: 'var(--brand-on)',
          transition: 'background var(--duration-fast) var(--ease-standard)',
        }}
      >
        {on ? <Icon name="Check" size={15} strokeWidth={3} /> : null}
      </span>
    </div>
  );
}
